import { forwardRef, useEffect, useRef, useState } from 'react';
import { SelectInputContainer } from './SelectInputContainer';
import { styledSelectInputContainer } from './StyledSelectInputContainer';
import { interstateInputEventBuilder } from '../InterstateEvents';
import { TextInputReadOnly } from '../TextInput/TextInputReadOnly';
import { isFieldRequired } from '../Validation';
import { useValidator } from '../Validation/useValidator';
import { selectInputRuleSet } from './selectInputRuleSet';
import { mergeSxStyle } from '../common/mergeSxStyle';
import { Box } from '../Box';
import { InputLayout } from '../InputLayout/InputLayout';
import { jsx as _jsx } from "react/jsx-runtime";
export const SelectInput = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    disabled,
    displayDeselectOption = true,
    displayLabel = true,
    errorMessage,
    hasError,
    helpMessage,
    id,
    label,
    maxHeight,
    onBlur,
    onChange,
    options,
    optionsWidth,
    placeholder,
    name,
    required,
    readOnly,
    value,
    size = 'medium',
    sx,
    layout,
    customErrorMessages,
    ...rest
  } = props;
  const initClickAway = useRef(false);
  const [open, setOpen] = useState(undefined);
  const [selected, setSelected] = useState('');
  const {
    validationState,
    validator
  } = useValidator(Boolean(hasError), errorMessage, false, undefined, customErrorMessages, helpMessage, Boolean(isFieldRequired(required)), label);
  const selectInputStyle = mergeSxStyle(styledSelectInputContainer({
    layout
  }), sx);
  useEffect(() => {
    setSelected(value || '');
  }, [value]);
  useEffect(() => {
    if (open === false) {
      validator.validate(selected, {}, selectInputRuleSet, true);
    }
    if (selected) {
      validator.validate(selected, {}, selectInputRuleSet, true);
    }
  }, [open, selected]);
  useEffect(() => {
    validator.reset();
  }, [errorMessage, hasError, helpMessage, customErrorMessages]);
  const handleOnBlur = event => {
    if (!disabled && onBlur) {
      onBlur(interstateInputEventBuilder(name, selected, validationState.isValid, event, label, validationState.messageKey));
    }
  };
  const effectiveId = id || 'interstate-select-input';
  const handleOnKeyDown = e => {
    if (e.key === 'Tab') {
      handleClickAway(undefined);
    }
    if (e.key === 'ArrowDown') {
      setOpen(true);
    }
  };
  const handleClickAway = e => {
    if (!initClickAway.current) {
      return;
    } else {
      initClickAway.current = false;
    }
    if (e?.target?.className && typeof e.target.className === 'string') {
      // this classname should match the classname used in ListBoxItem
      if (e.target.className.includes('ids-list-box-group-header') || e.target.className.includes('ids-list-box-item')) {
        return;
      }
    }
    const newValidationState = validator.validate(selected, {}, selectInputRuleSet, true);
    if (!disabled && onBlur) {
      onBlur(interstateInputEventBuilder(name, selected, newValidationState.isValid, e, label, newValidationState.messageKey));
    }
    if (onChange && !newValidationState.isValid) {
      onChange(interstateInputEventBuilder(name, selected, newValidationState.isValid, e, label, newValidationState.messageKey));
    }
  };
  const handleOnFocus = () => {
    initClickAway.current = true;
    validator.reset();
  };
  return /*#__PURE__*/_jsx(Box, {
    className: `${effectiveId}-root`,
    "aria-disabled": disabled ? 'true' : 'false',
    "data-disabled": disabled ? 'true' : 'false',
    sx: sx,
    children: /*#__PURE__*/_jsx(InputLayout, {
      labelId: effectiveId,
      required: required,
      displayLabel: displayLabel,
      label: label,
      layout: layout || 'vertical',
      size: size,
      validationState: validationState,
      disabled: Boolean(disabled),
      readOnly: Boolean(readOnly),
      children: readOnly ? /*#__PURE__*/_jsx(TextInputReadOnly, {
        value: value,
        size: size
      }) : /*#__PURE__*/_jsx(SelectInputContainer, {
        "data-testid": rest['data-testid'],
        disabled: Boolean(disabled),
        readOnly: Boolean(readOnly),
        validationStateType: validationState?.type,
        displayDeselectOption: displayDeselectOption,
        maxHeight: maxHeight,
        id: effectiveId,
        name: name,
        onChange: onChange,
        options: options,
        optionsWidth: optionsWidth,
        placeholder: placeholder,
        hasError: hasError,
        required: isFieldRequired(required),
        selected: selected,
        open: open,
        setSelected: setSelected,
        setOpen: setOpen,
        size: size,
        label: label,
        onKeyDown: handleOnKeyDown,
        onFocus: handleOnFocus,
        onClickAway: handleClickAway
      })
    })
  });
});
SelectInput.displayName = 'SelectInput';